import { useEffect, useState } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { Box, CircularProgress, Typography } from "@mui/material";
import { theme } from "../../styles/Theme.styled";

export const ExpandableTable = ({
    columns: initialColumns,
    data: initialData,
    fetchSubRows,
    noDataMessage = "No data available for this selection",
}) => {
    const [expandedRow, setExpandedRow] = useState(null);
    const [data, setData] = useState(initialData);
    const [loadingRow, setLoadingRow] = useState(null);

    const handleExpand = async (row) => {
        const rowId = row.id;
        if (expandedRow !== rowId) {
            if (!row.original.subRows) {
                setLoadingRow(rowId);
                try {
                    const subRows = await fetchSubRows(row.original);
                    setData((prevData) =>
                        prevData.map((item) =>
                            item.id === row.original.id
                                ? { ...item, subRows }
                                : item,
                        ),
                    );
                } finally {
                    setLoadingRow(null);
                    setExpandedRow(rowId);
                }
            } else {
                setExpandedRow(rowId);
            }
        } else {
            setExpandedRow(null);
        }
    };

    useEffect(() => {
        setData(initialData);
        setExpandedRow(null);
        setLoadingRow(null);
    }, [initialData]);

    const table = useMaterialReactTable({
        columns: initialColumns || [],
        data,
        enableExpanding: true,
        enableExpandAll: false,
        enableGlobalFilter: false,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableStickyHeader: true,
        enableColumnActions: false,
        getSubRows: (row) => row.subRows,
        getRowCanExpand: (row) => !row.original.parentId,
        renderDetailPanel: ({ row }) => {
            if (row.original.subRows && row.original.subRows.length === 0) {
                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        p={3}
                        sx={{ backgroundColor: theme.palette.grey }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            {noDataMessage}
                        </Typography>
                    </Box>
                );
            }

            return null;
        },
        muiExpandButtonProps: ({ row }) => ({
            onClick: (e) => {
                e.stopPropagation();
                if (!loadingRow) handleExpand(row);
            },
            disabled: loadingRow === row.id,
            "aria-label": expandedRow === row.id ? "Collapse" : "Expand",
            sx: { visibility: row.original.parentId ? "hidden" : "visible" },
            children:
                loadingRow === row.id ? (
                    <CircularProgress size={20} />
                ) : undefined,
        }),
        muiTableContainerProps: {
            sx: {
                maxHeight: "800px",
                border: "1px solid #e0e0e0",
            },
        },
        muiTableBodyRowProps: ({ row }) => {
            const isExpanded = expandedRow == row.id;
            const isParent = !row.original.parentId;
            const isChild = row.original.parentId;

            return {
                sx: {
                    backgroundColor: isChild ? "#f5f5f5" : "inherit",
                    "& > .MuiTableCell-root": {
                        borderBottom:
                            isExpanded && isParent ? "0px" : undefined,
                    },
                },
            };
        },
        state: {
            expanded: expandedRow ? { [expandedRow]: true } : {},
        },
    });

    return <MaterialReactTable table={table} />;
};
